import React, { useState, useEffect } from 'react';
import { 
  SparklesIcon, 
  EyeIcon, 
  CpuChipIcon,
  PhotoIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface AnalysisLoadingProps {
  currentPhase?: 'upload' | 'analyze' | 'generate' | 'complete';
}

const GeminiAnalysisLoading: React.FC<AnalysisLoadingProps> = ({ 
  currentPhase = 'upload' 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);

  const phases = [
    {
      id: 'upload',
      title: 'Uploading Image',
      description: 'Securely uploading your image to the cloud',
      icon: PhotoIcon,
      color: 'blue',
      duration: 2000,
    },
    {
      id: 'analyze',
      title: 'AI Analysis in Progress',
      description: 'Gemini 2.5 Flash is analyzing your item with advanced computer vision',
      icon: CpuChipIcon,
      color: 'purple',
      duration: 8000,
    },
    {
      id: 'generate',
      title: 'Generating Reference Image',
      description: 'Creating comparison image using Google Imagen AI',
      icon: SparklesIcon,
      color: 'emerald',
      duration: 5000,
    },
    {
      id: 'complete',
      title: 'Analysis Complete',
      description: 'Preparing your detailed results',
      icon: CheckCircleIcon,
      color: 'green',
      duration: 1000,
    },
  ];

  useEffect(() => {
    const phaseIndex = phases.findIndex(phase => phase.id === currentPhase);
    setCurrentStep(phaseIndex);
  }, [currentPhase]);

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentPhase = () => phases[currentStep] || phases[0];
  const currentPhaseData = getCurrentPhase();
  const Icon = currentPhaseData.icon;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-700">
        {/* Header with animated gradient */}
        <div className={`bg-gradient-to-r from-${currentPhaseData.color}-600 to-${currentPhaseData.color}-700 px-8 py-6 relative overflow-hidden`}>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-white/20 p-3 rounded-full mr-4">
                <Icon className="h-8 w-8 text-white animate-pulse" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{currentPhaseData.title}</h2>
                <p className="text-white/90">{currentPhaseData.description}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center text-white/90 mb-1">
                <ClockIcon className="h-4 w-4 mr-1" />
                <span className="text-sm font-mono">{formatTime(elapsedTime)}</span>
              </div>
              <div className="text-xs text-white/75">Processing...</div>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="p-8">
          <div className="space-y-4">
            {phases.map((phase, index) => {
              const PhaseIcon = phase.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;

              return (
                <div key={phase.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
                    isCompleted
                      ? 'bg-green-900 border-green-400 text-green-300'
                      : isActive
                      ? `bg-${phase.color}-900 border-${phase.color}-400 text-${phase.color}-300`
                      : 'bg-gray-800 border-gray-600 text-gray-500'
                  }`}>
                    {isCompleted ? (
                      <CheckCircleIcon className="h-5 w-5" />
                    ) : (
                      <PhaseIcon className={`h-5 w-5 ${isActive ? 'animate-pulse' : ''}`} />
                    )}
                  </div>
                  
                  <div className="ml-4 flex-1">
                    <div className="flex items-center justify-between">
                      <p className={`font-medium ${
                        isCompleted ? 'text-green-300' : 
                        isActive ? `text-${phase.color}-300` : 
                        'text-gray-400'
                      }`}>
                        {phase.title}
                      </p>
                      {isActive && (
                        <div className="flex space-x-1">
                          <div className={`w-2 h-2 bg-${phase.color}-400 rounded-full animate-bounce`}></div>
                          <div className={`w-2 h-2 bg-${phase.color}-400 rounded-full animate-bounce`} style={{ animationDelay: '0.1s' }}></div>
                          <div className={`w-2 h-2 bg-${phase.color}-400 rounded-full animate-bounce`} style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{phase.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Overall Progress Bar */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-300">Overall Progress</span>
              <span className="text-sm text-gray-400">{Math.round(((currentStep + 1) / phases.length) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className={`bg-gradient-to-r from-${currentPhaseData.color}-500 to-${currentPhaseData.color}-600 h-2 rounded-full transition-all duration-500 ease-out`}
                style={{ width: `${((currentStep + 1) / phases.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Fun Facts */}
          <div className="mt-6 p-4 bg-gray-700 rounded-lg border border-gray-600">
            <h4 className="font-medium text-white mb-2 flex items-center">
              <EyeIcon className="h-4 w-4 mr-2 text-blue-400" />
              Did you know?
            </h4>
            <p className="text-sm text-gray-300">
              {currentStep === 0 && "Gemini 2.5 Flash can analyze over 1 million pixels in seconds with industry-leading accuracy."}
              {currentStep === 1 && "Our AI uses advanced computer vision to identify materials, condition, and market value patterns."}
              {currentStep === 2 && "Imagen AI generates reference images using state-of-the-art diffusion models."}
              {currentStep === 3 && "Your analysis includes market data from thousands of similar items worldwide."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeminiAnalysisLoading;
